import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const inputCls = 'w-full rounded-xl border px-3 py-2 text-sm outline-none';
const inputStyle = {
  borderColor: SYSTEM_COLORS.borderSoft,
  backgroundColor: SYSTEM_COLORS.shellSurface,
  color: NEUTRAL_COLORS.black,
};

const Label = ({ children, required }) => (
  <label className="block text-xs font-semibold mb-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>
    {children}{required && <span className="ml-0.5" style={{ color: SYSTEM_COLORS.danger }}>*</span>}
  </label>
);

const CloseBtn = ({ onClose }) => (
  <button
    type="button"
    onClick={onClose}
    className="flex items-center justify-center rounded-lg transition-colors flex-shrink-0"
    style={{ width: 32, height: 32, color: 'rgba(255,255,255,0.85)' }}
    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <X size={17} />
  </button>
);

const MilestoneModal = ({ isOpen, onClose, milestone, onSave, teamMembers }) => {
  const [formData, setFormData] = useState({
    title:       '',
    description: '',
    dueDate:     '',
    overseer:    '',
    priority:    'medium',
  });

  useEffect(() => {
    if (milestone) {
      setFormData({
        ...milestone,
        dueDate: (milestone.due_date || milestone.dueDate)
          ? new Date(milestone.due_date || milestone.dueDate).toISOString().split('T')[0]
          : '',
        overseer: milestone.overseer || '',
      });
    } else {
      setFormData({ title: '', description: '', dueDate: '', overseer: '', priority: 'medium' });
    }
  }, [milestone]);

  const set = (name) => (e) => setFormData(f => ({ ...f, [name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave(formData);
      onClose();
    } catch {
      toast.error('Failed to save milestone');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}
    >
      <div
        className="w-full max-w-lg flex flex-col rounded-2xl border overflow-hidden"
        style={{
          backgroundColor: SYSTEM_COLORS.shellSurface,
          borderColor: SYSTEM_COLORS.borderSoft,
          boxShadow: SYSTEM_COLORS.shadowStrong,
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          <h2 className="text-base font-bold text-white">
            {milestone ? 'Edit Milestone' : 'Add Milestone'}
          </h2>
          <CloseBtn onClose={onClose} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div>
              <Label required>Title</Label>
              <input
                type="text"
                value={formData.title}
                onChange={set('title')}
                placeholder="Milestone title…"
                className={inputCls}
                style={inputStyle}
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <textarea
                value={formData.description}
                onChange={set('description')}
                rows={3}
                placeholder="Brief description…"
                className="w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none"
                style={inputStyle}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label required>Due Date</Label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={set('dueDate')}
                  className={inputCls}
                  style={inputStyle}
                  required
                />
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  value={formData.priority}
                  onChange={set('priority')}
                  className={inputCls}
                  style={inputStyle}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div>
              <Label required>Person in Charge</Label>
              <select
                value={formData.overseer}
                onChange={set('overseer')}
                className={inputCls}
                style={inputStyle}
                required
              >
                <option value="">Select person in charge</option>
                {teamMembers?.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex justify-end gap-3 px-5 py-4 border-t flex-shrink-0"
            style={{ borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-4 py-2 text-sm font-semibold border"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-xl px-5 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: BRAND_COLORS.primary }}
            >
              {milestone ? 'Save Changes' : 'Create Milestone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MilestoneModal;
